import { useRouter } from 'expo-router';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Platform, Pressable } from 'react-native';


import { PickerInput } from '../form/picker-input';
import { Col } from '../layout/col';
import { Row } from '../layout/row';
import { AddressTypeahead, type AddressTypeaheadHandle } from '../search/address-typeahead';

import { useAlert } from '@/components/alert-provider';
import { DateInput } from '@/components/form/date-input';
import { AssociationTypeahead } from '@/components/search/association-typeahead';
import { UserTypeahead } from '@/components/search/user-typeahead';
import { Text, TextInput, Button, useTheme, Snackbar, KeyboardAwareScrollView, MarkdownTextInput } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { usePermissions } from '@/hooks/use-permissions';
import { accountingService } from '@/services/accounting-service';
import { workOrderFormStyles as styles } from '@/styles';
import { WorkOrderRecurrencePeriod } from '@/types/models';
import type { Account, Association, User, WorkOrderFormData, WorkOrderPriority, WorkOrderStatus } from '@/types/models';
import { accountLotFillValue } from '@/utils/account-display';
import {
	businessMsToDate, dueDayMs, momentToBusinessDate, todayBusinessDate, toBusinessMs,
} from '@/utils/date-utils';
import { ModelPartial } from '@/utils/partial-type';
import { buildSignature }  from '@/utils/unsaved-changes';
import type { FieldSchema } from '@/utils/unsaved-changes';
import { submittedCreatedAt } from '@/utils/work-order-field-policy';
import { nextOwnerFieldValue, ownerFullName, pickOwnerPerson, shouldPromptSavePhone } from '@/utils/work-order-owner-autofill';
import type { AccountOwnerPerson } from '@/utils/work-order-owner-autofill';

/** Pause before the single retry of a failed `?accountId=` prefill lookup. */
export const ACCOUNT_PREFILL_RETRY_MS = 1000;

/**
 * `createdAt` is a real moment, but the Date of Order picker is date-only and renders UTC
 * fields: seed it with the moment's ET day (or ET today), never the raw moment or
 * `new Date()` — either reads as TOMORROW after 8 p.m. Eastern.
 */
const createdAtPickerValue = (value: Date | string | null | undefined): Date => {
	const ms = value ? new Date(value).getTime() : NaN;
	return Number.isFinite(ms) ? momentToBusinessDate(ms) : todayBusinessDate();
};

/** A stored due date (business-day marker, or a legacy moment) → the DateInput seed for its ET day. */
const dueDateSeed = (value: unknown): Date | null => {
	const day = dueDayMs(value);
	return day === null ? null : businessMsToDate(day);
};

interface WorkOrderFormProps {
	initialValues?: ModelPartial<WorkOrderFormData>;
	isCreate?: boolean;
	showRecurrenceFields?: boolean;
	onSave: (data: ModelPartial<WorkOrderFormData>) => void;
	/**
	 * Optional secondary save handler for the "Save and add another" flow on
	 * the new-WO form. When provided, a second primary button renders next
	 * to Save and calls this instead of the primary `onSave`. Implementations
	 * are expected to reset the form in place (e.g. by bumping a `key` on the
	 * parent) so the user stays on the create screen (#116).
	 */
	onSaveAndAddAnother?: (data: ModelPartial<WorkOrderFormData>) => void;
	onCancel: () => void;
	isSubmitting?: boolean;
	submitButtonText?: string;
	/**
	 * Form field keys this user may not edit. Rendered read-only/disabled. The
	 * same set is enforced server-side, so leaving a value untouched is safe —
	 * the API drops it regardless. Source of truth: `getNonUpdatableWorkOrderFields`.
	 */
	readOnlyFields?: ReadonlySet<string>;
	/**
	 * Seed the lot from this account once the association is set — as if the user had picked
	 * it in the Job Location typeahead (address + owner). Seeds once; never re-seeds over edits.
	 */
	initialAccountId?: string;
	children?: React.ReactNode;
}

export const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
	initialValues = {},
	isCreate = false,
	showRecurrenceFields = false,
	onSave,
	onSaveAndAddAnother,
	onCancel,
	isSubmitting = false,
	submitButtonText = 'Save',
	readOnlyFields,
	initialAccountId,
	children,
}) => {
	const theme = useTheme();
	const router = useRouter();
	const { alert } = useAlert();
	const permissions = usePermissions();
	const { user } = useApi();

	// Track the last-saved (or initial) signature of form data to detect real changes
	const [lastSavedSig, setLastSavedSig] = useState<string>('');

	// Schema for this form's unsaved-changes detection
	const sigSchema: FieldSchema = useMemo(() => ({
		orderNumber: 'number',
		workRequested: 'string',
		workCompleted: 'string',
		status: 'string',
		priority: 'string',
		terms: 'string',
		jobLocation: 'string',
		ownerName: 'string',
		ownerPhone: 'string',
		associationId: 'id',
		assignedToId: 'id',
		dueDate: 'date',
		recurrencePeriod: 'string',
		recurrenceValue: 'number',
		createdAt: 'date',
		completedAt: 'date',
		estimatedCost: 'number',
		latitude: 'number',
		longitude: 'number',
	}), []);

	// Form state
	const [customOrderNumber, setCustomOrderNumber] = useState(initialValues.orderNumber ? String(initialValues.orderNumber) : '');
	const [workRequested, setWorkRequested] = useState(initialValues.workRequested || '');
	const [workCompleted, setWorkCompleted] = useState(initialValues.workCompleted || '');
	const [status, setStatus] = useState(initialValues.status || 'open');
	const [priority, setPriority] = useState(initialValues.priority || 'medium');
	const [dueDate, setDueDate] = useState<Date | null>(
		dueDateSeed(initialValues.dueDate),
	);
	const [createdAt, setCreatedAt] = useState<Date | null>(() => createdAtPickerValue(initialValues.createdAt));
	const [completedAt, setCompletedAt] = useState<number | undefined>(
		initialValues.completedAt
			? (typeof initialValues.completedAt === 'string'
				? new Date(initialValues.completedAt).getTime()
				: initialValues.completedAt)
			: undefined,
	);
	const [assignedUser, setAssignedUser] = useState<User | null>(initialValues.assignedTo || null);
	const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(
		initialValues.association || null,
	);
	const [terms, setTerms] = useState(initialValues.terms || '30 Days');
	const [jobLocation, setJobLocation] = useState(initialValues.jobLocation || '');
	const jobLocationTypeahead = useRef<AddressTypeaheadHandle>(null);
	const [estimatedCost, setEstimatedCost] = useState(initialValues.estimatedCost || '');
	const [ownerName, setOwnerName] = useState(initialValues.ownerName || '');
	const [ownerPhone, setOwnerPhone] = useState(initialValues.ownerPhone || '');
	const [recurrenceValue, setRecurrenceValue] = useState(() => {
		if (initialValues.recurrenceValue) return String(initialValues.recurrenceValue);
		if (initialValues.recurrenceIntervalMonths) return String(initialValues.recurrenceIntervalMonths);
		return '';
	});
	const [recurrencePeriod, setRecurrencePeriod] = useState<WorkOrderRecurrencePeriod>(() => {
		if (initialValues.recurrencePeriod) return initialValues.recurrencePeriod;
		if (initialValues.recurrenceIntervalMonths) return WorkOrderRecurrencePeriod.Months;
		return WorkOrderRecurrencePeriod.Months;
	});
	const hasInitialRecurrence = useMemo(() => {
		const value = initialValues.recurrenceValue;
		const legacy = initialValues.recurrenceIntervalMonths;
		return (typeof value === 'number' && value > 0) || (typeof legacy === 'number' && legacy > 0);
	}, [initialValues.recurrenceValue, initialValues.recurrenceIntervalMonths]);
	const showRecurrenceSection = isCreate ? showRecurrenceFields : hasInitialRecurrence;
	const [snoozeUntil, setSnoozeUntil] = useState<Date | null>(
		initialValues.snoozeUntil ? new Date(initialValues.snoozeUntil) : null,
	);
	const [savedToServerAt, setSavedToServerAt] = useState<number | undefined>(initialValues.savedToServerAt);
	const [latitude, setLatitude] = useState(initialValues.latitude || null);
	const [longitude, setLongitude] = useState(initialValues.longitude || null);

	// Account matched via the Job Location typeahead (issue #285). Used to
	// auto-fill the owner name/phone and to offer saving a newly-entered phone
	// back to the account's owner record. Transient — not persisted on the WO.
	const [matchedAccount, setMatchedAccount] = useState<Account | null>(null);
	const [matchedOwner, setMatchedOwner] = useState<AccountOwnerPerson | null>(null);
	// The owner name/phone the LAST account match filled in, so the next match can tell
	// its own stale fill (replace) from text the user typed (keep). See nextOwnerFieldValue.
	const autofilledOwner = useRef<{ name: string; phone: string } | null>(null);
	const accountSelectSeq = useRef(0);

	// Validation feedback state
	const [showValidationMessage, setShowValidationMessage] = useState(false);
	const [validationMessage, setValidationMessage] = useState('');

	// Reset form state when initialValues changes (i.e., when editing a different record)
	useEffect(() => {
		setCustomOrderNumber(initialValues.orderNumber ? String(initialValues.orderNumber) : '');
		setWorkRequested(initialValues.workRequested || '');
		setWorkCompleted(initialValues.workCompleted || '');
		setStatus(initialValues.status || 'open');
		setPriority(initialValues.priority || 'medium');
		setDueDate(dueDateSeed(initialValues.dueDate));
		setAssignedUser(initialValues.assignedTo || null);
		setSelectedAssociation(initialValues.association || null);
		setTerms(initialValues.terms || '');
		setJobLocation(initialValues.jobLocation || '');
		setCreatedAt(createdAtPickerValue(initialValues.createdAt));
		setEstimatedCost(initialValues.estimatedCost || '');
		setCompletedAt(
			initialValues.completedAt
				? (typeof initialValues.completedAt === 'string'
					? new Date(initialValues.completedAt).getTime()
					: initialValues.completedAt)
				: undefined,
		);
		setOwnerName(initialValues.ownerName || '');
		setOwnerPhone(initialValues.ownerPhone || '');
		autofilledOwner.current = null;
		setRecurrenceValue(() => {
			if (initialValues.recurrenceValue) return String(initialValues.recurrenceValue);
			if (initialValues.recurrenceIntervalMonths) return String(initialValues.recurrenceIntervalMonths);
			return '';
		});
		setRecurrencePeriod(() => {
			if (initialValues.recurrencePeriod) return initialValues.recurrencePeriod;
			if (initialValues.recurrenceIntervalMonths) return WorkOrderRecurrencePeriod.Months;
			return WorkOrderRecurrencePeriod.Months;
		});
		setSnoozeUntil(initialValues.snoozeUntil ? new Date(initialValues.snoozeUntil) : null);
		setSavedToServerAt(initialValues.savedToServerAt);
		setLatitude(initialValues.latitude || null);
		setLongitude(initialValues.longitude || null);
	}, [initialValues]);

	// Initialize last-saved signature from incoming values (with defaults) so
	// a fresh create form doesn't immediately appear as having unsaved changes.
	useEffect(() => {
		const initialRecurrenceValue = typeof initialValues.recurrenceValue === 'number'
			? initialValues.recurrenceValue
			: (typeof initialValues.recurrenceIntervalMonths === 'number'
				? initialValues.recurrenceIntervalMonths
				: undefined);

		const initSig = buildSignature({
			orderNumber: initialValues.orderNumber || undefined,
			workRequested: initialValues.workRequested || '',
			workCompleted: initialValues.workCompleted || '',
			status: initialValues.status || 'open',
			priority: initialValues.priority || 'medium',
			terms: initialValues.terms || '',
			jobLocation: initialValues.jobLocation || '',
			ownerName: initialValues.ownerName || '',
			ownerPhone: initialValues.ownerPhone || '',
			associationId: initialValues.association?.id,
			assignedToId: initialValues.assignedTo?.id,
			dueDate: dueDateSeed(initialValues.dueDate),
			recurrencePeriod: initialRecurrenceValue ? initialValues.recurrencePeriod : undefined,
			recurrenceValue: initialRecurrenceValue,
			createdAt: createdAtPickerValue(initialValues.createdAt),
			estimatedCost: ((): number | undefined => {
				const ec = initialValues.estimatedCost;
				if (ec == null) return undefined;
				if (typeof ec === 'number') return ec;
				const n = Number(String(ec));
				return Number.isFinite(n) ? n : undefined;
			})(),
			latitude: initialValues.latitude ?? null,
			longitude: initialValues.longitude ?? null,
		}, sigSchema);
		setLastSavedSig(initSig);
	}, [initialValues, sigSchema]);

	// Build a normalized snapshot for comparison
	const currentSig = useMemo(() => buildSignature({
		orderNumber: customOrderNumber ? parseInt(String(customOrderNumber)) : undefined,
		workRequested,
		workCompleted,
		status,
		priority,
		terms,
		jobLocation,
		ownerName,
		ownerPhone,
		associationId: selectedAssociation?.id,
		assignedToId: assignedUser?.id,
		dueDate,
		recurrencePeriod: recurrenceValue.trim() ? recurrencePeriod : undefined,
		recurrenceValue,
		createdAt,
		estimatedCost,
		latitude,
		longitude,
	}, sigSchema), [customOrderNumber, workRequested, workCompleted, status, priority, terms, jobLocation, ownerName, ownerPhone, selectedAssociation, assignedUser, dueDate, recurrencePeriod, recurrenceValue, createdAt, estimatedCost, latitude, longitude, sigSchema]);

	const hasUnsavedChanges = useCallback((): boolean => currentSig !== lastSavedSig, [currentSig, lastSavedSig]);

	// Validation state
	const isPendingStatus = status === 'pending_materials' || status === 'pending_vendor';
	const isValid = workRequested?.trim() && selectedAssociation && jobLocation?.trim();

	const getValidationMessage = (jobLocationOverride?: string): string => {
		const missingFields: string[] = [];
		const effectiveJobLocation = jobLocationOverride ?? jobLocation;

		if (!workRequested?.trim()) {
			missingFields.push('Work Requested');
		}
		if (!selectedAssociation) {
			missingFields.push('Association');
		}
		if (!effectiveJobLocation?.trim()) {
			missingFields.push('Job Location');
		}

		if (missingFields.length === 1) {
			return `Please complete the ${missingFields[0]} field.`;
		} else if (missingFields.length > 1) {
			const lastField = missingFields.pop();
			return `Please complete the following fields: ${missingFields.join(', ')} and ${lastField}.`;
		}

		return 'Please complete all required fields.';
	};

	/**
	 * Settle the Job Location typeahead before validating.
	 *
	 * Its free text commits on a blur that `TypeaheadSearch` defers by 200ms so a click
	 * on a dropdown option wins the race. Clicking Save inside that window therefore
	 * validated against an empty field the user could plainly see they had filled in,
	 * and the save silently no-opped behind a self-dismissing "Please complete the Job
	 * Location field". Flushing returns the value synchronously because the state
	 * update it triggers is not visible until the next render.
	 */
	const flushJobLocation = (): string => jobLocationTypeahead.current?.flushPendingText() ?? jobLocation;

	const attemptSave = (destination: ((data: ModelPartial<WorkOrderFormData>) => void) | undefined) => {
		if (!destination) return;
		const settledJobLocation = flushJobLocation();
		if (workRequested?.trim() && selectedAssociation && settledJobLocation?.trim()) {
			proceedWithSave(destination, settledJobLocation);
			return;
		}
		setValidationMessage(getValidationMessage(settledJobLocation));
		setShowValidationMessage(true);
	};

	const handleSaveAttempt = () => attemptSave(onSave);

	const handleSaveAndAddAnotherAttempt = () => attemptSave(onSaveAndAddAnother);

	const handleSubmit = (
		destination: (data: ModelPartial<WorkOrderFormData>) => void,
		// The just-flushed Job Location. `jobLocation` state still holds the pre-flush
		// value on this tick, so the caller passes the settled one through explicitly.
		settledJobLocation?: string,
	) => {
		const effectiveJobLocation = settledJobLocation ?? jobLocation;
		if (!(workRequested?.trim() && selectedAssociation && effectiveJobLocation?.trim())) return;

		const normalizedRecurrenceValue = (() => {
			const trimmed = recurrenceValue.trim();
			if (!trimmed) return null;
			const parsed = parseInt(trimmed, 10);
			if (!Number.isFinite(parsed) || parsed <= 0) return null;
			return parsed;
		})();

		const recurrenceValueForApi = (() => {
			if (!showRecurrenceSection) return undefined;
			if (isCreate) {
				return normalizedRecurrenceValue === null ? undefined : normalizedRecurrenceValue;
			}
			return normalizedRecurrenceValue;
		})();

		const recurrencePeriodForApi = (() => {
			if (!showRecurrenceSection) return undefined;
			if (isCreate) {
				return normalizedRecurrenceValue === null ? undefined : recurrencePeriod;
			}
			return normalizedRecurrenceValue === null ? null : recurrencePeriod;
		})();

		const formData: ModelPartial<WorkOrderFormData> = {
			orderNumber: customOrderNumber ? parseInt(String(customOrderNumber)) : undefined,
			workRequested: workRequested,
			workCompleted: workCompleted,
			status: status as WorkOrderStatus,
			priority: priority as WorkOrderPriority,
			dueDate: dueDate ? toBusinessMs(dueDate) : undefined,
			createdAt: submittedCreatedAt(createdAt, initialValues.createdAt),
			completedAt: completedAt,
			assignedTo: assignedUser,
			association: selectedAssociation,
			terms: terms,
			jobLocation: effectiveJobLocation,
			estimatedCost: estimatedCost ? parseFloat(String(estimatedCost)) : undefined,
			ownerName: ownerName,
			ownerPhone: ownerPhone,
			recurrencePeriod: recurrencePeriodForApi,
			recurrenceValue: recurrenceValueForApi,
			savedToServerAt: savedToServerAt,
			latitude: latitude ?? undefined,
			longitude: longitude ?? undefined,
			snoozeUntil: isPendingStatus && snoozeUntil ? snoozeUntil.toISOString() : undefined,
		};

		// If status is changed to completed and no completedAt is set, set it to now
		if (status === 'completed' && !completedAt) {
			formData.completedAt = Date.now();
		}

		// Update last-saved signature so navigation after save is not blocked
		const savedSig = buildSignature({
			orderNumber: formData.orderNumber,
			workRequested: formData.workRequested,
			workCompleted: formData.workCompleted,
			status: formData.status,
			priority: formData.priority,
			terms: formData.terms,
			jobLocation: formData.jobLocation,
			ownerName: formData.ownerName,
			ownerPhone: formData.ownerPhone,
			associationId: formData.association?.id,
			assignedToId: formData.assignedTo?.id,
			// The picker's Date, as `currentSig` sees it — `formData.dueDate` is the business-day marker.
			dueDate,
			recurrencePeriod: formData.recurrenceValue ? formData.recurrencePeriod : undefined,
			recurrenceValue: formData.recurrenceValue,
			// The picker's day, as `currentSig` sees it — `formData.createdAt` may be the stored moment.
			createdAt,
			completedAt: formData.completedAt,
			estimatedCost: formData.estimatedCost,
			latitude: formData.latitude,
			longitude: formData.longitude,
		}, sigSchema);
		// Only update signature on non-create forms; on create, wait for success
		if (!isCreate) setLastSavedSig(savedSig);
		destination(formData);
	};

	// When the Job Location typeahead resolves to an account, pull in the
	// owner's name & phone — replacing what an EARLIER match filled, never text the
	// user typed (#384) — and remember the account + owner so we can offer to save
	// a phone back.
	const applyOwnerAutofill = useCallback((name: string, phone: string) => {
		const last = autofilledOwner.current;
		setOwnerName((prev) => nextOwnerFieldValue(prev, last?.name ?? null, name));
		setOwnerPhone((prev) => nextOwnerFieldValue(prev, last?.phone ?? null, phone));
		autofilledOwner.current = name || phone ? { name, phone } : null;
	}, []);

	/** The `?accountId=` prefill failed twice — shown as a dismissible notice under Job Location. */
	const [accountPrefillFailed, setAccountPrefillFailed] = useState(false);

	/**
	 * Load an account's owner into the form. Resolves null when there is nothing to apply (no
	 * account, a newer pick, another association's lot, or — unless `rethrow` — a failed lookup).
	 * With `rethrow`, a failed lookup rejects instead, so the `?accountId=` seed can retry it.
	 */
	const handleAccountSelect = useCallback(async (
		accountId?: string,
		expectedAssociationId?: string,
		rethrow = false,
	): Promise<Account | null> => {
		const seq = ++accountSelectSeq.current;
		if (!accountId) {
			setMatchedAccount(null);
			setMatchedOwner(null);
			applyOwnerAutofill('', '');
			return null;
		}
		try {
			const [account, peopleResp] = await Promise.all([
				accountingService.getAccount(accountId),
				accountingService.listPeople({ accountId }),
			]);
			// A newer pick landed while this one was in flight — its owner wins.
			if (seq !== accountSelectSeq.current) return null;
			// A stale or hand-edited URL can pair an association with another association's lot.
			if (expectedAssociationId && account.associationId !== expectedAssociationId) return null;
			const owner = pickOwnerPerson(peopleResp.data);
			setMatchedAccount(account);
			setMatchedOwner(owner);
			const name = ownerFullName(owner) || account.primaryOwnerName || '';
			const phone = owner?.phone?.trim() ? owner.phone ?? '' : '';
			applyOwnerAutofill(name, phone);
			setAccountPrefillFailed(false);
			return account;
		} catch (err) {
			// A superseded lookup's failure is moot: the newer pick owns the fields, so never hand
			// it to a caller that would retry over that pick.
			if (rethrow && seq === accountSelectSeq.current) throw err;
			console.warn('owner auto-fill lookup failed:', err);
			return null;
		}
	}, [applyOwnerAutofill]);

	// Unmount-only (not the seed effect's cleanup: `seededAccountId` means a cancelled seed never
	// re-runs), so a pending prefill retry can tell the form is gone.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	// `?accountId=` from an account page: seed the lot once. Waits for the association
	// because its arrival runs the initialValues reset above, which would wipe an earlier seed.
	// A failed lookup is retried once; a second failure says so next to Job Location rather
	// than leaving the fields silently empty.
	const seededAccountId = useRef<string | null>(null);
	const seedAssociationId = initialValues.association?.id;
	useEffect(() => {
		if (!initialAccountId || !seedAssociationId || seededAccountId.current) return;
		seededAccountId.current = initialAccountId;
		void (async () => {
			let account: Account | null = null;
			// Our attempt's sequence number, read SYNCHRONOUSLY at call time: a pick the user makes
			// while it is in flight bumps it, and their pick must win over any retry of ours.
			const firstAttempt = handleAccountSelect(initialAccountId, seedAssociationId, true);
			const mySeq = accountSelectSeq.current;
			try {
				account = await firstAttempt;
			} catch (firstErr) {
				if (!mountedRef.current || accountSelectSeq.current !== mySeq) return;
				console.warn('account prefill failed, retrying:', firstErr);
				await new Promise((resolve) => setTimeout(resolve, ACCOUNT_PREFILL_RETRY_MS));
				if (!mountedRef.current || accountSelectSeq.current !== mySeq) return;
				try {
					account = await handleAccountSelect(initialAccountId, seedAssociationId, true);
				} catch (err) {
					console.warn('account prefill failed:', err);
					if (mountedRef.current) setAccountPrefillFailed(true);
					return;
				}
			}
			if (!mountedRef.current) return;
			// Fill what the typeahead's own account pick fills — never a raw multi-line address.
			const lot = account ? accountLotFillValue(account) : '';
			// Never over text the user already typed while the lookup was in flight.
			if (lot) setJobLocation((prev) => prev || lot);
		})();
	}, [initialAccountId, seedAssociationId, handleAccountSelect]);

	// Save the work order's phone onto the matched account. The server fills a
	// blank only (sets a missing owner phone, or creates the owner when none
	// exists), so this is safe for PMs. Best-effort — a failure surfaces a notice
	// but never blocks the work-order save.
	const persistPhoneToAccount = useCallback(
		async (account: Account, phone: string, name: string) => {
			try {
				await accountingService.saveOwnerContact(account.id, { phone, name: name.trim() || undefined });
			} catch (err) {
				console.warn('failed to save phone to account:', err);
				setValidationMessage('Could not save the phone number to the account.');
				setShowValidationMessage(true);
			}
		},
		[],
	);

	// Gate the actual save behind the "save phone to account?" prompt when the
	// user entered a phone for an account whose owner doesn't have one yet, and
	// they're allowed to write it back (#285). Roles without the permission just
	// save the work order — no prompt destined to 403.
	const proceedWithSave = (
		destination: (data: ModelPartial<WorkOrderFormData>) => void,
		settledJobLocation?: string,
	) => {
		const phone = ownerPhone.trim();
		const account = matchedAccount;
		if (
			account
			&& permissions.canManageOwnerContact
			&& shouldPromptSavePhone({ matchedAccountId: account.id, enteredPhone: phone, ownerPerson: matchedOwner })
		) {
			const acctLabel = account.accountNumber || account.address || 'this account';
			alert(
				'Save phone number?',
				`Save this phone number to ${acctLabel}?`,
				[
					{ text: 'No', style: 'cancel', onPress: () => handleSubmit(destination, settledJobLocation) },
					{
						text: 'Yes',
						onPress: async () => {
							await persistPhoneToAccount(account, phone, ownerName);
							handleSubmit(destination, settledJobLocation);
						},
					},
				],
				{ cancelable: false },
			);
			return;
		}
		handleSubmit(destination, settledJobLocation);
	};

	// Disabled unsaved changes navigation prevention
	// useEffect(() => {
	// 	console.log('Setting up Expo Router navigation prevention...');

	// 	// Override router methods to check for unsaved changes
	// 	const originalPush = router.push;
	// 	const originalReplace = router.replace;
	// 	const originalBack = router.back;
	// 	const originalNavigate = router.navigate;

	// 	const checkAndNavigate = (originalMethod: (...args: any[]) => void, ...args: any[]) => {
	// 		console.log('Expo Router navigation attempt intercepted');

	// 		if (!isSubmitting && hasUnsavedChanges()) {
	// 			console.log('Preventing Expo Router navigation due to unsaved changes');
	// 			alert(
	// 				'Unsaved Changes',
	// 				'You have unsaved changes. Are you sure you want to leave without saving?',
	// 				[
	// 					{ text: 'Stay', style: 'cancel', onPress: () => console.log('User chose to stay') },
	// 					{
	// 						text: 'Leave',
	// 						style: 'destructive',
	// 						onPress: () => {
	// 							console.log('User chose to leave, proceeding with navigation');
	// 							originalMethod.apply(router, args);
	// 						},
	// 					},
	// 				],
	// 			);
	// 		} else {
	// 			console.log('No unsaved changes, allowing Expo Router navigation');
	// 			originalMethod.apply(router, args);
	// 		}
	// 	};

	// 	// Override router methods
	// 	router.push = (...args: any[]) => checkAndNavigate(originalPush, ...args);
	// 	router.replace = (...args: any[]) => checkAndNavigate(originalReplace, ...args);
	// 	router.back = (...args: any[]) => checkAndNavigate(originalBack, ...args);
	// 	router.navigate = (...args: any[]) => checkAndNavigate(originalNavigate, ...args);

	// 	// Cleanup function to restore original methods
	// 	return () => {
	// 		console.log('Restoring original Expo Router methods');
	// 		router.push = originalPush;
	// 		router.replace = originalReplace;
	// 		router.back = originalBack;
	// 		router.navigate = originalNavigate;
	// 	};
	// }, [router, hasUnsavedChanges, alert, isSubmitting]);

	// Handle browser refresh/close (web only).
	//
	// Refs keep the beforeunload handler stable while still reading the
	// latest values — without them, every keystroke would change the
	// `currentSig` memo → rebuild `hasUnsavedChanges` → tear the listener
	// down and re-add it (observed as a 50+ cycle log flood per keystroke).
	const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
	const isSubmittingRef = useRef(isSubmitting);
	useEffect(() => { hasUnsavedChangesRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);
	useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);

	useEffect(() => {
		if (Platform.OS !== 'web') return;

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (!isSubmittingRef.current && hasUnsavedChangesRef.current()) {
				e.preventDefault();
				e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
				return 'You have unsaved changes. Are you sure you want to leave?';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, []);

	// Initialize lastSavedSig from initialValues so clean forms don't warn
	useEffect(() => {
		const initialRecurrenceValue = typeof initialValues.recurrenceValue === 'number'
			? initialValues.recurrenceValue
			: (typeof initialValues.recurrenceIntervalMonths === 'number'
				? initialValues.recurrenceIntervalMonths
				: undefined);

		const initSig = buildSignature({
			workRequested: initialValues.workRequested,
			workCompleted: initialValues.workCompleted,
			status: initialValues.status,
			priority: initialValues.priority,
			terms: initialValues.terms,
			jobLocation: initialValues.jobLocation,
			ownerName: initialValues.ownerName,
			ownerPhone: initialValues.ownerPhone,
			associationId: initialValues.association?.id,
			assignedToId: initialValues.assignedTo?.id,
			dueDate: dueDateSeed(initialValues.dueDate),
			createdAt: createdAtPickerValue(initialValues.createdAt),
			estimatedCost: initialValues.estimatedCost,
			recurrencePeriod: initialRecurrenceValue ? initialValues.recurrencePeriod : undefined,
			recurrenceValue: initialRecurrenceValue,
			latitude: initialValues.latitude,
			longitude: initialValues.longitude,
		}, sigSchema);
		setLastSavedSig(initSig);
	}, [initialValues, sigSchema]);

	return (
		<KeyboardAwareScrollView style={styles.container} extraScrollHeight={100} testID="work-order-form">
			<Row style={{ position: 'relative', zIndex: 30 }}>
				<Col size={9}>
					<AssociationTypeahead
						label="Association *"
						// Archived/inactive associations are never pickable, for ANY role — a data rule,
						// not a permission. An existing work order on an archived association still shows
						// it: the selection is seeded from `initialValues.association`, not this search.
						status="active"
						value={selectedAssociation}
						onSelect={(association) => {
							setSelectedAssociation(association);
						}}
						placeholder="Search for an association..."
						testID="work-order-association-input"
						autoFocus={isCreate}
						filterAssociations={(associations) => {
							// Property Managers can only select associations assigned to them, unless they
							// hold association_admin — the same rule the PUT enforces (api/work-orders.ts).
							if (permissions.isPropertyManager && !permissions.isAssociationAdmin) {
								return associations.filter(a => a.assignedTo === user?.id);
							}
							return associations;
						}}
					/>
				</Col>
				<Col size={3}>
					<DateInput
						label="Date of Order"
						value={createdAt}
						onChange={setCreatedAt}
						disabled={readOnlyFields?.has('createdAt')}
						utcDay
						testID="work-order-date-of-order-input"
					/>
				</Col>
			</Row>
			<Row style={{ position: 'relative', zIndex: 20 }}>
				<Col size={6}>
					<TextInput
						mode="outlined"
						label="Terms"
						value={terms}
						onChangeText={(text) => {
							setTerms(text);
						}}
						testID="work-order-terms-input"
					/>
				</Col>
				<Col size={6}>
					<View style={{ position: 'relative', zIndex: 10 }}>
						<AddressTypeahead
							ref={jobLocationTypeahead}
							label='Job Location *'
							value={jobLocation}
							onChange={setJobLocation}
							onLocationChange={(lat, lng) => {
								setLatitude(lat);
								setLongitude(lng);
							}}
							onAccountSelect={handleAccountSelect}
							associationId={selectedAssociation?.id}
							showShortcuts={true}
							testID="work-order-job-location-input"
						/>
					</View>
					{accountPrefillFailed && (
						<View
							testID="work-order-account-prefill-notice"
							role="alert"
							style={{
								flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6,
								paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6,
								backgroundColor: '#fff8e1', borderWidth: 1, borderColor: '#ffe082',
							}}
						>
							<Text style={{ flex: 1, color: '#6d4c00', fontSize: 13 }}>
								Couldn&apos;t load this lot&apos;s details — pick it in Job Location.
							</Text>
							<Pressable
								onPress={() => setAccountPrefillFailed(false)}
								accessibilityRole="button"
								testID="work-order-account-prefill-notice-dismiss"
							>
								<Text style={{ color: '#6d4c00', fontSize: 13, fontWeight: '600' }}>Dismiss</Text>
							</Pressable>
						</View>
					)}
				</Col>
			</Row>

			<Row>
				<Col size={6}>
					<TextInput
						mode="outlined"
						label="Owner Name"
						value={ownerName}
						onChangeText={setOwnerName}
						testID="work-order-owner-name-input"
					/>
				</Col>
				<Col size={6}>
					<TextInput
						mode="outlined"
						label="Owner Phone"
						value={ownerPhone}
						onChangeText={setOwnerPhone}
						testID="work-order-owner-phone-input"
					/>
				</Col>
			</Row>

			{showRecurrenceSection && (
				<>
					<Row>
						<Col size={6}>
							<TextInput
								mode="outlined"
								label="Recurs Every"
								value={recurrenceValue}
								onChangeText={setRecurrenceValue}
								placeholder="e.g. 3"
								keyboardType="numeric"
								testID="work-order-recurrence-value-input"
							/>
						</Col>
						<Col size={6}>
							<PickerInput
								label="Recurrence Period"
								value={recurrencePeriod}
								onChange={(value) => {
									switch (value) {
										case WorkOrderRecurrencePeriod.Days:
										case WorkOrderRecurrencePeriod.Weeks:
										case WorkOrderRecurrencePeriod.Months:
											setRecurrencePeriod(value);
											break;
										default:
											setRecurrencePeriod(WorkOrderRecurrencePeriod.Months);
									}
								}}
								options={[
									{ value: WorkOrderRecurrencePeriod.Days, label: 'Days' },
									{ value: WorkOrderRecurrencePeriod.Weeks, label: 'Weeks' },
									{ value: WorkOrderRecurrencePeriod.Months, label: 'Months' },
								]}
								testID="work-order-recurrence-period-select"
							/>
						</Col>
					</Row>
					<Row>
						<Col size={12}>
							<Text style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
								When this work order is marked completed, a new work order will be auto-created with a due date {recurrenceValue ? `${recurrenceValue} ${recurrencePeriod}` : 'N days/weeks/months'} later.
							</Text>
						</Col>
					</Row>
				</>
			)}

			<MarkdownTextInput
				mode="outlined"
				label="Work Requested *"
				value={workRequested}
				onChangeText={setWorkRequested}
				multiline
				numberOfLines={4}
				testID="work-order-work-requested-input"
			/>

			{!isCreate && (
				<MarkdownTextInput
					mode="outlined"
					label="Work Completed"
					value={workCompleted}
					onChangeText={setWorkCompleted}
					multiline
					numberOfLines={4}
					testID="work-order-work-completed-input"
				/>
			)}

			<Row>
				<Col size={6}>
					<PickerInput
						label="Status"
						value={status}
						onChange={setStatus}
						options={[
							{ value: 'quote', label: 'Quote' },
							{ value: 'open', label: 'Open' },
							{ value: 'in_progress', label: 'In Progress' },
							{ value: 'pending_materials', label: 'Pending Materials' },
							{ value: 'pending_vendor', label: 'Pending Vendor' },
							{ value: 'completed', label: 'Completed' },
							{ value: 'cancelled', label: 'Cancelled' },
						]}
						testID="work-order-status-select"
					/>
				</Col>
				<Col size={6}>
					<PickerInput
						label="Priority"
						value={priority}
						onChange={setPriority}
						options={[
							{ value: 'low', label: 'Low' },
							{ value: 'medium', label: 'Medium' },
							{ value: 'high', label: 'High' },
							{ value: 'urgent', label: 'Urgent' },
						]}
						testID="work-order-priority-select"
					/>
				</Col>
			</Row>

			<Row style={{ position: 'relative', zIndex: 20 }}>
				<Col size={6}>
					<UserTypeahead
						label="Assigned To"
						value={assignedUser}
						onSelect={(user) => {
							setAssignedUser(user);
						}}
						placeholder="Search for a user..."
						testID="work-order-assigned-to-input"
					/>
				</Col>
				<Col size={6}>
					<DateInput
						label="Due Date"
						value={dueDate}
						onChange={setDueDate}
						utcDay
					/>
				</Col>
			</Row>
			{!isCreate && status === 'completed' && (
				<Row>
					<Col size={12}>
						<DateInput
							label="Completed Date"
							value={completedAt ? new Date(completedAt) : null}
							onChange={(date) => setCompletedAt(date ? date.getTime() : undefined)}
						/>
					</Col>
				</Row>
			)}

			{isPendingStatus && (
				<Row>
					<Col size={12}>
						<DateInput
							label="Snooze Until"
							value={snoozeUntil}
							onChange={setSnoozeUntil}
						/>
					</Col>
				</Row>
			)}

			{status === 'quote' && (
				<TextInput
					mode="outlined"
					label="Estimated Cost ($)"
					value={String(estimatedCost || '')}
					onChangeText={setEstimatedCost}
					keyboardType="decimal-pad"
					testID="work-order-estimated-cost-input"
				/>
			)}

			{/* Follow-up needed is flagged at completion time — see
			    components/work-orders/edit-work-completed-button.tsx (#48). */}

			{/*(status === 'completed' || status === 'cancelled') && (
				<View style={styles.section}>
					<Text style={styles.label}>Server Synchronization</Text>
					{savedToServerAt ? (
						<Text style={{ fontSize: 16 }}>
							Saved to server on: <DateDisplay date={savedToServerAt} />
						</Text>
					) : (
						<Button
							mode="contained"
							onPress={() => setSavedToServerAt(Date.now())}
							style={{ marginTop: 8 }}
						>
							<Text>Save to Server</Text>
						</Button>
					)}
				</View>
			)*/}

			<View style={styles.buttonContainer}>
				<Button
					mode="outlined"
					onPress={onCancel}
					style={styles.button}
					textColor={theme.colors.onSurfaceVariant}
					testID="work-order-cancel-button"
				>
					<Text>Cancel</Text>
				</Button>
				<Button
					mode="contained"
					onPress={handleSaveAttempt}
					loading={isSubmitting}
					disabled={isSubmitting}
					style={styles.button}
					testID="work-order-save-button"
				>
					{submitButtonText}
				</Button>
				{onSaveAndAddAnother && (
					<Button
						mode="contained"
						onPress={handleSaveAndAddAnotherAttempt}
						loading={isSubmitting}
						disabled={isSubmitting}
						style={styles.button}
						testID="work-order-save-and-add-another-button"
					>
						<Text>Save &amp; add another</Text>
					</Button>
				)}
			</View>
			{children}

			<Snackbar
				visible={showValidationMessage}
				onDismiss={() => setShowValidationMessage(false)}
				duration={4000}
				action={{
					label: 'OK',
					onPress: () => setShowValidationMessage(false),
				}}
			>
				{validationMessage}
			</Snackbar>
		</KeyboardAwareScrollView>
	);
};
